// Importações do Firebase
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from "firebase/functions";

// Configuração do seu Firebase
const firebaseConfig = {
  apiKey: "AIzaSyBtTMredHxBCb_Tx8xr-I7tNfFcmPDkE4Y",
  authDomain: "fechamento-semanal.firebaseapp.com",
  projectId: "fechamento-semanal",
  storageBucket: "fechamento-semanal.appspot.com",
  messagingSenderId: "488194585891",
  appId: "1:488194585891:web:9c482785fa8c8ad654c896",
  measurementId: "G-07QP7EYM7Z"
};

// Inicializa o Firebase
const app = initializeApp(firebaseConfig);

// Exporta os serviços
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// Connect to Firebase Emulators in development
if (window.location.hostname === 'localhost') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.log('🔧 Connected to Firebase Functions Emulator on localhost:5001');
}

// Helper para chamar cloud functions
export const callFunction = async (functionName, data = {}) => {
  const callable = httpsCallable(functions, functionName);
  const result = await callable(data);
  return result.data;
};

// Helper para chamar funções via HTTP (Easypanel backend em produção, Firebase local)
// Usado para Xero e WhatsApp
export const callXeroFunction = async (functionName, data = {}, options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    console.error('❌ No user authenticated');
    throw new Error('User must be authenticated');
  }

  console.log(`🔐 Calling ${functionName} as user:`, user.email);
  const token = await user.getIdToken();
  console.log(`🎫 Token obtained (length: ${token.length})`);

  // Map function names to routes
  const routeMap = {
    'xeroAuthStatusV1': '/xero/auth/status',
    'xeroGetClients': '/xero/clients',
    'xeroGetInvoiceData': '/xero/invoice/data',
    'xeroSendInvoice': '/xero/invoice/send',
    'whatsappSendConfirmations': '/whatsapp/send',
    'sendDocumentsEmailBackend': '/documents/send-email',
    'getClients': '/api/clients',
    'createClient': '/api/clients',
    'updateClient': '/api/clients',
    'deleteClient': '/api/clients',
    // Feedback Manager
    'getFeedbacks': '/api/feedbacks',
    'getFeedbackStats': '/api/feedbacks/stats',
    'cancelFeedback': '/api/feedbacks',
    'markFeedbackReceived': '/api/feedbacks',
    'reactivateFeedback': '/api/feedbacks',
    'clearFeedbackTestData': '/api/feedbacks/clear-test-data',
    // WhatsApp Prompts
    'getWhatsappPrompts': '/api/whatsapp-prompts',
    'getArchivedWhatsappPrompts': '/api/whatsapp-prompts',
    'createWhatsappPrompt': '/api/whatsapp-prompts',
    'updateWhatsappPrompt': '/api/whatsapp-prompts',
    'deleteWhatsappPrompt': '/api/whatsapp-prompts',
    'archiveWhatsappPrompt': '/api/whatsapp-prompts',
    'unarchiveWhatsappPrompt': '/api/whatsapp-prompts',
    // Email Marketing
    'massEmail': '/api/mass-email',
    'massEmailPreview': '/api/mass-email/preview',
    // Personal Events (Admin Agenda)
    'getPersonalEvents': '/api/personal-events',
    'createPersonalEvent': '/api/personal-events',
    'updatePersonalEvent': '/api/personal-events',
    'deletePersonalEvent': '/api/personal-events',
    // Agenda Multi-User (Admin)
    'getAgendaUsers': '/api/agenda/users',
    'deactivateAgendaUser': '/api/agenda/users',
    'reactivateAgendaUser': '/api/agenda/users',
    'getAgendaInvites': '/api/agenda/invites',
    'createAgendaInvite': '/api/agenda/invites',
    'cancelAgendaInvite': '/api/agenda/invites',
    // Xero Quotes
    'getXeroQuotes': '/api/xero/quotes',
    'createXeroQuote': '/api/xero/quotes',
    'updateXeroQuote': '/api/xero/quotes',
    'deleteXeroQuote': '/api/xero/quotes',
    'sendXeroQuoteEmail': '/api/xero/quotes',
    'getXeroQuotePdf': '/api/xero/quotes',
    // Payroll
    'getPayrollEmployees': '/api/payroll/employees',
    'getPayrollSettings': '/api/payroll/settings',
    'getPayrollMappings': '/api/payroll/mappings',
    'createPayrollMapping': '/api/payroll/mappings',
    'deletePayrollMapping': '/api/payroll/mappings',
    'approvePayroll': '/api/payroll/approve',
    'unapprovePayroll': '/api/payroll/unapprove',
    'calculatePayroll': '/api/payroll/calculate',
    'createPayrun': '/api/payroll/create-payrun',
    'approvePayrun': '/api/payroll/approve-payrun',
    'getPayruns': '/api/payroll/payruns',
    'getPayslips': '/api/payroll/payslips',
    'getPayrollRecords': '/api/payroll/records',
    'getPayrollHistory': '/api/payroll/history',
    // Collaborator Week Import
    'getCollaboratorWeekImport': '/api/collaborator/week-import',
    // Users
    'getUsers': '/api/users',
    // Custom Members (external team members)
    'getCustomMembers': '/api/teams/custom-members',
    // Invoice History
    'xeroOverdueInvoices': '/xero/invoices/overdue',
    'syncClientsToPostgres': '/sync/clients-to-postgres',
    'sendInvoiceReminders': '/whatsapp/send-invoice-reminders',
    // Export
    'exportAllData': '/api/export/all-data'
  };

  const route = routeMap[functionName] || `/${functionName}`;

  // Determine HTTP method
  const methodMap = {
    'getClients': 'GET',
    'createClient': 'POST',
    'updateClient': 'PUT',
    'deleteClient': 'DELETE',
    'massEmailPreview': 'GET',
    'getPersonalEvents': 'GET',
    'getAgendaUsers': 'GET',
    'getAgendaInvites': 'GET',
    'getXeroQuotes': 'GET',
    'getPayrollEmployees': 'GET',
    'getPayrollSettings': 'GET',
    'getPayrollMappings': 'GET',
    'getPayruns': 'GET',
    'getPayrollRecords': 'GET',
    'getPayrollHistory': 'GET',
    'getUsers': 'GET',
    'getCollaboratorWeekImport': 'GET',
    'getCustomMembers': 'GET',
    'exportAllData': 'GET'
  };
  const method = options.method || methodMap[functionName] || 'POST';

  // Build URL (add ID for PUT/DELETE, add action if provided)
  let url = `https://api.aitomize.space${route}`;
  if (options.id) {
    url += `/${options.id}`;
  }
  if (options.action) {
    url += `/${options.action}`;
  }
  // Add query params for GET requests
  if (method === 'GET' && Object.keys(data).length > 0) {
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
  }

  // Add explicit queryParams for any method (e.g., DELETE with mode=single|future)
  if (options.queryParams && Object.keys(options.queryParams).length > 0) {
    const params = new URLSearchParams();
    Object.entries(options.queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });
    if (params.toString()) {
      url += (url.includes('?') ? '&' : '?') + params.toString();
    }
  }

  console.log(`📞 Calling ${functionName} (${method}) at: ${url}`);

  const fetchOptions = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };

  // Only add body for POST/PUT
  if (method !== 'GET' && method !== 'DELETE') {
    fetchOptions.body = JSON.stringify(data);
  }

  const response = await fetch(url, fetchOptions);

  console.log(`📡 Response status: ${response.status}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    console.error(`❌ Error from ${functionName}:`, error);
    throw new Error(error.error || 'Request failed');
  }

  const result = await response.json();
  console.log(`✅ Success from ${functionName}:`, result);
  return result;
};

// Helper para executar migração Firestore → PostgreSQL
// Faz a migração diretamente no frontend (tem acesso ao Firestore)
export const migrateFromFirestore = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.error('❌ No user authenticated');
    throw new Error('User must be authenticated');
  }

  console.log('🔄 Starting Firestore migration (frontend)...');
  const token = await user.getIdToken();

  // Importar dependências do Firestore
  const { collection, getDocs } = await import('firebase/firestore');

  // 1. Buscar todos os clientes do Firestore
  console.log('📥 Fetching clients from Firestore...');
  const clientsSnapshot = await getDocs(collection(db, 'clients'));
  console.log(`✅ Found ${clientsSnapshot.size} clients in Firestore`);

  // 2. Buscar TODOS os clientes do PostgreSQL (uma vez só)
  console.log('📥 Fetching clients from PostgreSQL...');
  const getResponse = await fetch('https://api.aitomize.space/api/clients', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!getResponse.ok) {
    throw new Error('Failed to fetch clients from PostgreSQL');
  }

  const { clients: pgClients } = await getResponse.json();
  console.log(`✅ Found ${pgClients.length} clients in PostgreSQL`);

  let updated = 0;
  let created = 0;
  let errors = 0;
  const errorDetails = [];

  // 3. Para cada cliente do Firestore
  for (const doc of clientsSnapshot.docs) {
    const firestoreData = doc.data();
    const firestoreId = doc.id;

    try {

      // Encontrar cliente no PostgreSQL (por firebase_id, email ou phone)
      let pgClient = pgClients.find(c => c.firebase_id === firestoreId);
      if (!pgClient && firestoreData.email) {
        pgClient = pgClients.find(c => c.email?.toLowerCase() === firestoreData.email.toLowerCase());
      }
      if (!pgClient && firestoreData.phone) {
        pgClient = pgClients.find(c => c.phone === firestoreData.phone);
      }

      // Preparar dados para atualização
      const clientData = {
        firebase_id: firestoreId,
        name: firestoreData.name || 'Unknown',
        email: firestoreData.email?.toLowerCase() || null,
        phone: firestoreData.phone || null,
        address: firestoreData.address || null,
        service_duration: firestoreData.hours || null,
        default_price: firestoreData.defaultInvoiceAmount || null,
        password: firestoreData.password || null,
        payment_method: firestoreData.paymentMethod || 'cash',
        default_checklist: JSON.stringify(firestoreData.defaultChecklist || firestoreData.checklist || []),
        restricted_times: firestoreData.restrictedTimes ? JSON.stringify(firestoreData.restrictedTimes) : null,
        notes: firestoreData.notes || null,
        xero_contact_id: firestoreData.xeroContactId || null,
        team: firestoreData.team || null,
        invoice_description: firestoreData.invoiceDescription || null
      };

      if (pgClient) {
        // UPDATE existing client
        const updateResponse = await fetch(`https://api.aitomize.space/api/clients/${pgClient.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(clientData)
        });

        if (!updateResponse.ok) {
          throw new Error(`Failed to update client ${firestoreData.name}`);
        }

        updated++;
        console.log(`✅ [${updated + created}/${clientsSnapshot.size}] Updated: ${firestoreData.name}`);
      } else {
        // CREATE new client
        const createResponse = await fetch('https://api.aitomize.space/api/clients', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(clientData)
        });

        if (!createResponse.ok) {
          throw new Error(`Failed to create client ${firestoreData.name}`);
        }

        created++;
        console.log(`➕ [${updated + created}/${clientsSnapshot.size}] Created: ${firestoreData.name}`);
      }

    } catch (error) {
      errors++;
      errorDetails.push({ name: firestoreData.name || firestoreId, error: error.message });
      console.error(`❌ Error processing ${firestoreData.name || firestoreId}:`, error.message);
    }
  }

  const result = {
    success: true,
    summary: {
      total: clientsSnapshot.size,
      updated,
      created,
      errors
    },
    errorDetails: errors > 0 ? errorDetails : undefined
  };

  console.log('✅ Migration completed:', result);
  return result;
};
